// @ts-check
// Tela Visitas (/visits, contrato 08 de visitas, seção 7): para quem recebe as famílias. Lista agrupada por dia ou
// calendário (mês e semana, com as grades da Agenda), filtros por período, unidade, quem conduz, situação e tipo.
// Visão, datas e filtros ficam na URL; o filtro de quem conduz é lembrado no navegador.
import { html } from 'htm/preact';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { OPPORTUNITY_EVENT_TYPES } from '../../../../shared/calendar/event-types.js';
import { navigate, searchParamsState, updateSearchParams } from '../../app/router.js';
import { listVisits } from '../../data/visits.js';
import { findManyRecords } from '../../data/rest.js';
import { objectsByNameSingular } from '../../data/metadata-store.js';
import { loadWorkspaceMembers, workspaceMembersState } from '../../data/workspace-members.js';
import { addDays, formatRangeTitle, formatTime, fromDateKey, getVisibleRange, isSameDay, shiftAnchor, startOfDay, toDateKey } from '../../lib/calendar-dates.js';
import { formatFullName } from '../../lib/field-metadata.js';
import { canReadObjectRecords } from '../../lib/permissions.js';
import { readStorage, writeStorage } from '../../lib/utils.js';
import { PageCard, PageHeader } from '../page/page-card.js';
import { Button, LightIconButton } from '../../ui/button.js';
import { Tag } from '../../ui/chip.js';
import { EmptyState } from '../../ui/feedback.js';
import { Icon } from '../../ui/icon.js';
import { showErrorSnackbar } from '../../ui/snackbar.js';
import { MonthView, TimeGridView } from '../calendar/calendar-views.js';
import { LOCALE, t } from '../../i18n/index.js';

/** @typedef {import('../../data/visits.js').VisitItem} VisitItem */

const VIEWS = /** @type {const} */ (['list', 'month', 'week']);
// lista: hoje e os próximos 7 dias por padrão; a semana e o mês seguem a data da URL
const LIST_RANGES = /** @type {const} */ (['next7', 'today', 'thisWeek', 'thisMonth', 'last30']);
export const VISIT_SITUATIONS = /** @type {const} */ (['SCHEDULED', 'ATTENDED', 'NO_SHOW']);
const SITUATION_COLORS = { SCHEDULED: 'sky', ATTENDED: 'green', NO_SHOW: 'red' };
const HOST_STORAGE_KEY = 'appcrm.visits.host';
const HOST_EVERYONE = '';

/** @param {string | null} value */
const readView = (value) => /** @type {typeof VIEWS[number]} */ (VIEWS.includes(/** @type {any} */ (value)) ? value : 'list');

/** @param {string | null} value */
const readAnchor = (value) => (value && /^\d{4}-\d{2}-\d{2}$/.test(value) ? fromDateKey(value) : new Date());

/** @param {typeof LIST_RANGES[number]} range @param {Date} now */
function listRange(range, now) {
  const today = startOfDay(now);
  switch (range) {
    case 'today': return { start: today, end: addDays(today, 1) };
    case 'thisWeek': return getVisibleRange('week', now);
    case 'thisMonth': return { start: new Date(now.getFullYear(), now.getMonth(), 1), end: new Date(now.getFullYear(), now.getMonth() + 1, 1) };
    case 'last30': return { start: addDays(today, -30), end: addDays(today, 1) };
    default: return { start: today, end: addDays(today, 8) };
  }
}

/** @param {string} iso */
function dayKeyOf(iso) {
  return toDateKey(new Date(iso));
}

/** @param {Date} day */
function formatDayHeading(day) {
  const today = startOfDay(new Date());
  if (isSameDay(day, today)) return t('visits.today');
  if (isSameDay(day, addDays(today, 1))) return t('visits.tomorrow');
  if (isSameDay(day, addDays(today, -1))) return t('visits.yesterday');
  const text = new Intl.DateTimeFormat(LOCALE, { weekday: 'long', day: 'numeric', month: 'long' }).format(day);
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** @param {{ situation: string, needsCheckIn?: boolean }} props */
export function VisitSituationTag({ situation, needsCheckIn = false }) {
  if (needsCheckIn) return html`<${Tag} color="amber" text=${t('visits.needsCheckIn')} />`;
  return html`<${Tag} color=${/** @type {any} */ (SITUATION_COLORS)[situation] ?? 'gray'} text=${t(`visits.situations.${situation}`)} />`;
}

/** @param {string} eventType */
export const eventTypeColor = (eventType) => OPPORTUNITY_EVENT_TYPES.find((type) => type.key === eventType)?.color ?? 'gray';

/** @param {{ visit: VisitItem }} props */
function VisitRow({ visit }) {
  const studentsText = visit.students.map((student) => (student.grade ? `${student.name} (${student.grade})` : student.name)).join(', ');
  return html`<li>
    <button type="button" class="visit-row" data-situation=${visit.situation} onClick=${() => navigate(`/visits/${visit.enrollmentOpportunityId}?type=${visit.eventType}`)}>
      <span class="visit-row-time">${formatTime(visit.startsAt)}</span>
      <span class="visit-row-main">
        <span class="visit-row-title">
          <${Tag} color=${eventTypeColor(visit.eventType)} text=${t(`calendar.eventTypes.${visit.eventType}`)} />
          <span class="visit-row-family">${visit.family?.name ?? visit.contact?.name ?? visit.card.name}</span>
        </span>
        <span class="visit-row-students">${studentsText || t('visits.noStudents')}</span>
        <span class="visit-row-meta">
          ${visit.campus ? html`<span><${Icon} name="IconBuildingCommunity" size=${14} /> ${visit.campus.name}</span>` : null}
          <span><${Icon} name="IconDoorEnter" size=${14} /> ${visit.host?.name ?? t('visits.noHost')}</span>
        </span>
      </span>
      <span class="visit-row-status"><${VisitSituationTag} situation=${visit.situation} needsCheckIn=${visit.needsCheckIn} /></span>
      <${Icon} name="IconChevronRight" size=${16} className="visit-row-chevron" />
    </button>
  </li>`;
}

/** @param {{ visits: VisitItem[] }} props */
function VisitList({ visits }) {
  const groups = useMemo(() => {
    /** @type {Map<string, VisitItem[]>} */
    const byDay = new Map();
    for (const visit of visits) byDay.set(dayKeyOf(visit.startsAt), [...(byDay.get(dayKeyOf(visit.startsAt)) ?? []), visit]);
    return [...byDay.entries()];
  }, [visits]);
  const todayKey = toDateKey(new Date());
  return html`<div class="visit-list" data-testid="visit-list">
    ${groups.map(([dayKey, dayVisits]) => html`<section key=${dayKey} class="visit-day" data-today=${dayKey === todayKey ? 'true' : undefined}>
      <h3 class="visit-day-heading">
        <span>${formatDayHeading(fromDateKey(dayKey))}</span>
        <span class="visit-day-count">${t('visits.count', { count: dayVisits.length })}</span>
      </h3>
      <ul class="visit-day-items">${dayVisits.map((visit) => html`<${VisitRow} key=${visit.id} visit=${visit} />`)}</ul>
    </section>`)}
  </div>`;
}

/** @param {VisitItem} visit @returns {import('../../data/calendar.js').CalendarItem} */
const toCalendarItem = (visit) => ({
  id: visit.id, kind: 'OPPORTUNITY_DATE', eventType: visit.eventType, source: 'OPPORTUNITY',
  title: visit.family?.name ?? visit.contact?.name ?? visit.card.name, description: null, location: null, startsAt: visit.startsAt, endsAt: visit.endsAt,
  isFullDay: false, isCanceled: visit.situation === 'NO_SHOW', visibility: 'SHARE_EVERYTHING', isRestricted: false, fieldName: visit.fieldName,
  enrollmentOpportunity: { id: visit.card.id, name: visit.card.name, stage: visit.card.stage }, organizer: null, participants: [], connectedAccounts: [],
  href: null,
});

/**
 * Chips de filtro de múltipla escolha (vazio = todos).
 * @param {{ label: string, options: Array<{ key: string, text: string, color?: string }>, selected: string[], onChange: (next: string[]) => void }} props
 */
function FilterChips({ label, options, selected, onChange }) {
  return html`<div class="visit-filter-chips" role="group" aria-label=${label}>
    ${options.map((option) => {
      const isActive = selected.includes(option.key);
      return html`<button
        type="button"
        key=${option.key}
        class="visit-filter-chip"
        aria-pressed=${isActive ? 'true' : 'false'}
        data-active=${isActive ? 'true' : undefined}
        onClick=${() => onChange(isActive ? selected.filter((key) => key !== option.key) : [...selected, option.key])}
      >
        ${option.color ? html`<span class="calendar-legend-dot" style=${{ background: `var(--t-color-${option.color})` }}></span>` : null}
        ${option.text}
      </button>`;
    })}
  </div>`;
}

export function VisitsPage() {
  const parameters = searchParamsState.value;
  const view = readView(parameters.get('view'));
  const anchor = readAnchor(parameters.get('date'));
  const rangeKey = /** @type {typeof LIST_RANGES[number]} */ (LIST_RANGES.includes(/** @type {any} */ (parameters.get('range'))) ? parameters.get('range') : 'next7');
  const host = parameters.get('host') ?? readStorage(HOST_STORAGE_KEY) ?? HOST_EVERYONE;
  const campusId = parameters.get('campus') ?? '';
  const situations = (parameters.get('situations') ?? '').split(',').filter((value) => VISIT_SITUATIONS.includes(/** @type {any} */ (value)));
  const eventTypes = (parameters.get('types') ?? '').split(',').filter((value) => OPPORTUNITY_EVENT_TYPES.some((type) => type.key === value));
  const [visits, setVisits] = useState(/** @type {VisitItem[] | null} */ (null));
  const [campuses, setCampuses] = useState(/** @type {Array<{ id: string, name: string }>} */ ([]));
  const requestRef = useRef(0);
  const range = useMemo(
    () => (view === 'list' ? listRange(rangeKey, new Date()) : getVisibleRange(view, anchor)),
    [view, rangeKey, toDateKey(anchor)],
  );
  const members = workspaceMembersState.value ?? [];
  const campusObject = objectsByNameSingular.value.get('campus');

  useEffect(() => {
    document.title = t('visits.title');
    loadWorkspaceMembers();
    if (campusObject && canReadObjectRecords(campusObject)) {
      findManyRecords(campusObject, { limit: 100, depth: 0 })
        .then(({ records }) => setCampuses(records.map((record) => ({ id: record.id, name: record.name ?? '' }))))
        .catch(() => setCampuses([]));
    }
  }, []);

  useEffect(() => {
    const requestId = (requestRef.current += 1);
    setVisits(null);
    listVisits({ startsAt: range.start.toISOString(), endsAt: range.end.toISOString(), campusId, visitHostId: host || null, situations, eventTypes })
      .then((loaded) => {
        if (requestId === requestRef.current) setVisits(loaded);
      })
      .catch((error) => {
        if (requestId !== requestRef.current) return;
        showErrorSnackbar(error);
        setVisits([]);
      });
  }, [range.start.getTime(), range.end.getTime(), host, campusId, situations.join(','), eventTypes.join(',')]);

  const setHost = (/** @type {string} */ value) => {
    writeStorage(HOST_STORAGE_KEY, value);
    updateSearchParams({ host: value });
  };
  const openSummary = (/** @type {import('../../data/calendar.js').CalendarItem} */ item) => {
    const visit = (visits ?? []).find((candidate) => candidate.id === item.id);
    if (visit) navigate(`/visits/${visit.enrollmentOpportunityId}?type=${visit.eventType}`);
  };
  const counts = useMemo(() => ({
    total: (visits ?? []).length,
    today: (visits ?? []).filter((visit) => isSameDay(new Date(visit.startsAt), new Date())).length,
    pending: (visits ?? []).filter((visit) => visit.needsCheckIn).length,
  }), [visits]);

  const toolbar = html`<div class="calendar-toolbar visits-toolbar">
    <div class="calendar-toolbar-navigation">
      ${view === 'list'
        ? html`<label class="calendar-owner-filter">
            <${Icon} name="IconCalendarEvent" size=${14} />
            <select class="calendar-owner-select" aria-label=${t('visits.filters.period')} value=${rangeKey}
              onChange=${(event) => updateSearchParams({ range: /** @type {HTMLSelectElement} */ (event.currentTarget).value })}>
              ${LIST_RANGES.map((option) => html`<option key=${option} value=${option}>${t(`visits.ranges.${option}`)}</option>`)}
            </select>
          </label>`
        : html`
            <${Button} title=${t('calendar.today')} variant="secondary" size="small" onClick=${() => updateSearchParams({ date: toDateKey(new Date()) })} />
            <${LightIconButton} icon="IconChevronLeft" ariaLabel=${t('calendar.previous')} onClick=${() => updateSearchParams({ date: toDateKey(shiftAnchor(view, anchor, -1)) })} />
            <${LightIconButton} icon="IconChevronRight" ariaLabel=${t('calendar.next')} onClick=${() => updateSearchParams({ date: toDateKey(shiftAnchor(view, anchor, 1)) })} />
            <h2 class="calendar-toolbar-title">${formatRangeTitle(view, anchor)}</h2>`}
    </div>
    <div class="calendar-toolbar-filters">
      <label class="calendar-owner-filter">
        <${Icon} name="IconDoorEnter" size=${14} />
        <select class="calendar-owner-select" aria-label=${t('visits.filters.host')} value=${host}
          onChange=${(event) => setHost(/** @type {HTMLSelectElement} */ (event.currentTarget).value)}>
          <option value=${HOST_EVERYONE}>${t('visits.filters.everyone')}</option>
          <option value="ME">${t('visits.filters.mine')}</option>
          <option value="NONE">${t('visits.filters.noHost')}</option>
          ${members.map((member) => html`<option key=${member.id} value=${member.id}>${formatFullName(member.name) || member.userEmail}</option>`)}
        </select>
      </label>
      ${campuses.length > 0
        ? html`<label class="calendar-owner-filter">
            <${Icon} name="IconBuildingCommunity" size=${14} />
            <select class="calendar-owner-select" aria-label=${t('visits.filters.campus')} value=${campusId}
              onChange=${(event) => updateSearchParams({ campus: /** @type {HTMLSelectElement} */ (event.currentTarget).value || null })}>
              <option value="">${t('visits.filters.allCampuses')}</option>
              ${campuses.map((campus) => html`<option key=${campus.id} value=${campus.id}>${campus.name}</option>`)}
            </select>
          </label>`
        : null}
      <div class="segmented-control calendar-view-switch" role="tablist" aria-label=${t('visits.title')}>
        ${VIEWS.map((option) => html`<button
          type="button"
          key=${option}
          role="tab"
          class="segmented-item"
          aria-selected=${view === option ? 'true' : 'false'}
          data-active=${view === option ? 'true' : undefined}
          onClick=${() => updateSearchParams({ view: option })}
        >${t(`visits.views.${option}`)}</button>`)}
      </div>
    </div>
  </div>`;

  const filters = html`<div class="calendar-legend visits-filters">
    <${FilterChips}
      label=${t('visits.filters.situation')}
      options=${VISIT_SITUATIONS.map((key) => ({ key, text: t(`visits.situations.${key}`), color: /** @type {any} */ (SITUATION_COLORS)[key] }))}
      selected=${situations}
      onChange=${(next) => updateSearchParams({ situations: next.length > 0 ? next.join(',') : null })}
    />
    <span class="visits-filters-divider" aria-hidden="true"></span>
    <${FilterChips}
      label=${t('visits.filters.eventType')}
      options=${OPPORTUNITY_EVENT_TYPES.map((type) => ({ key: type.key, text: t(`calendar.eventTypes.${type.key}`), color: type.color }))}
      selected=${eventTypes}
      onChange=${(next) => updateSearchParams({ types: next.length > 0 ? next.join(',') : null })}
    />
  </div>`;

  const summaryLine = visits && visits.length > 0
    ? html`<div class="visits-counters" aria-live="polite">
        <span>${t('visits.count', { count: counts.total })}</span>
        ${counts.today > 0 ? html`<span data-tone="today">${t('visits.todayCount', { count: counts.today })}</span>` : null}
        ${counts.pending > 0 ? html`<span data-tone="pending">${t('visits.pendingCount', { count: counts.pending })}</span>` : null}
      </div>`
    : null;

  let body;
  if (view === 'list') {
    body = visits === null
      ? html`<div class="visits-loading">${t('visits.loading')}</div>`
      : visits.length === 0
        ? html`<${EmptyState} icon="IconDoorEnter" title=${t('visits.empty')} subtitle=${t('visits.emptyHint')} />`
        : html`${summaryLine}<${VisitList} visits=${visits} />`;
  } else {
    const items = (visits ?? []).map(toCalendarItem);
    body = html`<div class="calendar-body" data-loading=${visits === null ? 'true' : undefined}>
      ${view === 'month'
        ? html`<${MonthView} anchor=${anchor} items=${items} selectedId=${null} onSelect=${openSummary} onOpenDay=${(day) => updateSearchParams({ view: 'week', date: toDateKey(day) })} />`
        : html`<${TimeGridView} view="week" anchor=${anchor} items=${items} selectedId=${null} onSelect=${openSummary}
            onOpenDay=${(day) => updateSearchParams({ date: toDateKey(day) })} onCreateAt=${() => {}} />`}
    </div>`;
  }

  return html`<${PageCard} className="calendar-page-card visits-page-card" header=${html`<${PageHeader} icon="IconDoorEnter" title=${t('visits.title')} />`}>
    <div class="calendar-page visits-page" data-testid="visits-page" data-view=${view}>
      ${toolbar}
      ${filters}
      ${body}
    </div>
  <//>`;
}
